import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  leadingIcon?: ReactNode;
  fullWidth?: boolean;
};

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-forja-blue-gradient text-white shadow-glow-blue hover:shadow-glow-blue hover:brightness-110 active:brightness-95",
  secondary:
    "bg-forja-surface2 text-white border border-forja-border hover:bg-forja-surface2/80",
  ghost:
    "bg-transparent text-white/80 hover:text-white hover:bg-white/5",
  danger:
    "bg-red-600 text-white hover:bg-red-700",
};

// Piso de altura por tamaño. Sin esto el botón mide lo que su texto más el
// padding: `sm` quedaba en ~30px y `md` en ~37, los dos por debajo de los 44 que
// necesita un control que se opera con el dedo. Se venía parcheando pantalla por
// pantalla; el arreglo va acá, que es donde nacen.
//
// Móvil primero: la clase base es la del teléfono (44) y `sm:` la relaja en
// escritorio, donde el mouse es preciso y 44 se ve enorme en botones chicos.
const sizeClasses: Record<Size, string> = {
  sm: "text-xs px-3 py-1.5 rounded-md min-h-[44px] sm:min-h-[32px]",
  md: "text-sm px-4 py-2 rounded-lg min-h-[44px] sm:min-h-[38px]",
  lg: "text-base px-5 py-3 rounded-lg font-semibold min-h-[48px]",
};

export function Button({
  variant = "primary",
  size = "md",
  children,
  leadingIcon,
  fullWidth = false,
  className = "",
  disabled,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={disabled}
      className={[
        "inline-flex items-center justify-center gap-2 font-medium transition-all duration-150",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none",
        variantClasses[variant],
        sizeClasses[size],
        fullWidth ? "w-full" : "",
        className,
      ].join(" ")}
    >
      {leadingIcon ? <span className="-ml-0.5">{leadingIcon}</span> : null}
      {children}
    </button>
  );
}
